import {
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import { ExperimentsAPI } from '@wix/editorx-core-api';
import type { HostAPI } from '../types';

import type { VirtualElementProvider } from '../api/providers/VirtualElementProvider';
import type { ManifestStore, PanelStore } from '../stores';
import type { ManifestLifecycle } from '../lifecycle';
import { LayoutPlatformService } from './LayoutPlatformService';
import { BehaviorsPlatformService } from './BehaviorsPlatformService';
import { StatesPlatformService } from './StatesPlatformService';
import { HierarchyPlatformService } from './HierarchyPlatformService';
import { ApplicationDataPlatformService } from './ApplicationDataPlatformService';
import { ComponentsDefinitionsPlatformService } from './ComponentsDefinitionsPlatformService';
import { EditActionsPlatformService } from './EditActionsPlatformService';
import { PanelsPlatformService } from './PanelsPlatformService';
import { ActionableElementsPlatformService } from './ActionableElementsPlatformService';
import { IVirtualManifestBuilderProps } from '../api';
import { ArrayItemsPlatformService } from './ArrayItemsPlatformService';
import { MenuItemsPlatformService } from './MenuItemsPlatformService';
import { ManifestPlatformService } from './ManifestPlatformService';
import { ManifestChangeDetection } from '../ManifestChangeDetection';
import { DefinitionDescriptionsPlatformService } from './DefinitionDescriptionsPlatformService';
import { InnerElementPathPlatformService } from './InnerElementPathPlatformService';
import { ThemePlatformService } from './ThemePlatformService';
import { DisplayVisibilityPlatformService } from './DisplayVisibilityPlatformService';
import { PresetsPlatformService } from './PresetsPlatformService';
import { ManifestServiceStats } from '../ManifestServiceStats';
import { BindingsPlatformService } from './BindingsPlatformService';
import { ContainerPlatformService } from './ContainerPlatformService';
import { CapabilitiesPlatformService } from './CapabilitiesPlatformService';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';
import { ManifestLoadingTracker } from './ManifestLoadingTracker';
import { ClientSpecMapService } from '../sharedServices';
import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

const DEFAULT_MANIFEST_WAIT_TIMEOUT = 3000;

export class ComponentsPlatformService {
  public readonly layout: LayoutPlatformService;
  public readonly behaviors: BehaviorsPlatformService;
  public readonly states: StatesPlatformService;
  public readonly hierarchy: HierarchyPlatformService;
  public readonly applicationData: ApplicationDataPlatformService;
  public readonly components: ComponentsDefinitionsPlatformService;
  public readonly containers: ContainerPlatformService;
  public readonly manifest: ManifestPlatformService;
  public readonly editActions: EditActionsPlatformService;
  public readonly panels: PanelsPlatformService;
  public readonly actionableElements: ActionableElementsPlatformService;
  public readonly arrayItems: ArrayItemsPlatformService;
  public readonly menuItems: MenuItemsPlatformService;
  public readonly definitionDescriptions: DefinitionDescriptionsPlatformService;
  public readonly innerElementPath: InnerElementPathPlatformService;
  public readonly theme: ThemePlatformService;
  public readonly displayVisibility: DisplayVisibilityPlatformService;
  public readonly presets: PresetsPlatformService;
  public readonly bindings: BindingsPlatformService;
  public readonly capabilities: CapabilitiesPlatformService;
  public readonly experimentsAPI: ExperimentsAPI;

  private readonly virtualElementProvider: VirtualElementProvider;
  private readonly manifestLifecycle: ManifestLifecycle;
  private readonly manifestStore: ManifestStore;
  private readonly manifestLoadingTracker: ManifestLoadingTracker;

  constructor(
    dm: DocumentServicesObject,
    hostAPI: HostAPI,
    virtualElementProvider: VirtualElementProvider,
    manifestStore: ManifestStore,
    panelStore: PanelStore,
    manifestLifecycle: ManifestLifecycle,
    editorPointerAPI: EditorPointerServiceAPI,
    manifestChangeDetection: ManifestChangeDetection,
    manifestServiceStats: ManifestServiceStats,
    experimentsAPI: ExperimentsAPI,
    clientSpecMapService: ClientSpecMapService,
  ) {
    const args = [
      dm,
      hostAPI,
      virtualElementProvider,
      manifestStore,
      panelStore,
      editorPointerAPI,
      manifestChangeDetection,
      manifestServiceStats,
      experimentsAPI,
    ] as const;

    this.manifestLoadingTracker = new ManifestLoadingTracker(
      manifestStore,
      clientSpecMapService,
    );

    this.virtualElementProvider = virtualElementProvider;
    this.manifestLifecycle = manifestLifecycle;
    this.manifestStore = manifestStore;
    this.experimentsAPI = experimentsAPI;

    this.layout = new LayoutPlatformService(...args);
    this.behaviors = new BehaviorsPlatformService(...args);
    this.arrayItems = new ArrayItemsPlatformService(...args);
    this.menuItems = new MenuItemsPlatformService(...args);
    this.hierarchy = new HierarchyPlatformService(...args);
    this.states = new StatesPlatformService(...args);
    this.applicationData = new ApplicationDataPlatformService(...args);
    this.components = new ComponentsDefinitionsPlatformService(...args);
    this.containers = new ContainerPlatformService(...args);
    this.manifest = new ManifestPlatformService(...args);
    this.editActions = new EditActionsPlatformService(...args);
    this.panels = new PanelsPlatformService(...args);
    this.actionableElements = new ActionableElementsPlatformService(...args);
    this.definitionDescriptions = new DefinitionDescriptionsPlatformService(
      ...args,
    );
    this.innerElementPath = new InnerElementPathPlatformService(...args);
    this.theme = new ThemePlatformService(...args);
    this.displayVisibility = new DisplayVisibilityPlatformService(...args);
    this.presets = new PresetsPlatformService(...args);
    this.bindings = new BindingsPlatformService(...args);
    this.capabilities = new CapabilitiesPlatformService(...args);
  }

  // Used on the app installation step to load the manifest and panels
  async loadAppManifestAndPanels(appData: EditorClientSpecMapEntry) {
    await this.manifestLifecycle.handleApp(appData);
  }

  /**
   * NOTE: This method is intended for debugging purposes only.
   */
  getAllManifests() {
    return this.manifestStore.getAll();
  }

  async getPendingAppsManifests() {
    await this.manifestLifecycle.loadPendingManifests();

    const allManifests = this.getAllManifests();

    return Object.fromEntries(
      Object.entries(allManifests).filter(([_, value]) => value.isPending),
    );
  }

  buildManifestAPI(props: IVirtualManifestBuilderProps) {
    /*
      This method is kept for backward compatibility.
      New code shouldn't use this directly
     */
    return this.virtualElementProvider.buildVirtualElement(props).getAPI();
  }

  /**
   * Waits for component manifests to be loaded.
   * - If all manifests exist, resolve immediately.
   * - If components are known (in CSM or currently loading), waits.
   * - If any component is unknown, logs a warning and skips it.
   */
  async waitForManifests(compTypes: string[], options?: { timeout?: number }) {
    const pendingTypes = compTypes.filter(
      (type) => !this.manifestStore.has(type),
    );

    if (pendingTypes.length === 0) {
      return;
    }

    const knownPendingTypes = pendingTypes.filter((compType) => {
      const isKnown =
        this.manifestLifecycle.isManifestLoading(compType) ||
        this.manifestLoadingTracker.isComponentTypesKnown([compType]);

      if (!isKnown) {
        console.warn(
          `Component type '${compType}' is not known – no manifest to wait for.`,
        );
      }

      return isKnown;
    });

    if (knownPendingTypes.length === 0) {
      return;
    }

    const timeout = Math.max(
      0,
      Math.min(
        options?.timeout ?? DEFAULT_MANIFEST_WAIT_TIMEOUT,
        DEFAULT_MANIFEST_WAIT_TIMEOUT,
      ),
    );

    await Promise.race([
      Promise.all(
        knownPendingTypes.map((type) => {
          if (this.manifestStore.has(type)) {
            return Promise.resolve();
          }

          return this.manifestLoadingTracker.waitForManifest(type);
        }),
      ),
      new Promise<void>((_, reject) =>
        setTimeout(() => {
          reject(
            createEditorPlatformManifestServicesError(
              EditorPlatformManifestServicesErrorCode.ManifestWaitTimeout,
              `Timeout waiting for manifests: ${knownPendingTypes.join(', ')}`,
            ),
          );
        }, timeout),
      ),
    ]);
  }

  async waitForAllManifestsReady() {
    return this.manifestLoadingTracker.waitForAllPending();
  }
}
